/* eslint-disable import/prefer-default-export */
import { defineStore } from "pinia";
import axios from "axios";
import { yyyymmdddash, parseLocalDate } from "@/services/utils";
export const useReportSettingsStore = defineStore("reportSettingsStore", {
  state: () => ({
    saving: false,
    alerts: {},
    company: {
      settings: {
        reports: {}
      }
    }
  }),
  actions: {
    async updateMyCompany(settings) {
      try {
        const response = await axios({
          url: `/me/company`,
          method: "PUT",
          data: settings
        });
        this.$patch({
          company: response.data
        });
        this.alerts.success("Your settings were saved");
      } catch (error) {
        this.alerts.error(error.response.data.message);
      }
    },
    update(state) {
      const percentTotal = (state.originatingContributionFirm || 0) + (state.workingContribution || 0) + (state.originatingContribution || 0) + (state.responsibleContribution || 0);
      if (percentTotal > 100) {
        this.alerts.error(`The revenue allocations cannot exceed 100% total and they are ${percentTotal}%`);
        return;
      }
      if (state.accounts.find(a => a.originationTotal > 100)) {
        this.alerts.error(`The total origination for a user cannot exceed 100%`);
        return;
      }
      this.$patch({
        saving: true
      });
      state.useMostRecentDateCutoff = state.useMostRecentDateCutoff ? yyyymmdddash(parseLocalDate(state.useMostRecentDateCutoff)) : state.useMostRecentDateCutoff;
      state.accounts = state.accounts.filter(a => !(a.origination === undefined && a.origination === '') || a.revenueToResponsible || a.revenueToExpenses || !(a.workingContribution === undefined && a.workingContribution === '') || a.referrals && a.referrals.length);
      this.updateMyCompany({
        settings: {
          report: state,
          fixedFeeAllocationMode: state.fixedFeeAllocationMode
        }
      });
      this.$patch({
        saving: false
      });
    },
    allocationLabel(setting) {
      if (setting.revenueToResponsible) return "Responsible";
      if (setting.revenueToExpenses) return "Expenses";
      return "Self";
    },
    reportForm(state) {
      state.me.company.settings.report = state.me.company.settings.report || {};
      const form = {};
      const {
        originatingContribution,
        responsibleContribution,
        originatingContributionFirm,
        workingContribution,
        useWorkingPercentage,
        originationStepDownPeriodInMonths,
        servicesToResponsible,
        chargesAttributedToResponsible,
        unallocatedToOriginating,
        creditsAsRevenue,
        useMostRecentDate,
        useMostRecentDateCutoff,
        accounts
      } = state.me.company.settings.report;
      const {
        fixedFeeAllocationMode
      } = state.me.company.settings;
      form.originatingContribution = originatingContribution;
      form.responsibleContribution = responsibleContribution;
      form.originatingContributionFirm = originatingContributionFirm;
      form.workingContribution = workingContribution || 100 - ((originatingContributionFirm || 0) + (originatingContribution || 0) + (responsibleContribution || 0));
      state.me.company.settings.report.workingContribution = form.workingContribution;
      form.useWorkingPercentage = useWorkingPercentage;
      form.originationStepDownPeriodInMonths = originationStepDownPeriodInMonths;
      form.servicesToResponsible = servicesToResponsible;
      form.chargesAttributedToResponsible = chargesAttributedToResponsible;
      form.unallocatedToOriginating = unallocatedToOriginating;
      form.creditsAsRevenue = creditsAsRevenue;
      form.useMostRecentDate = !!useMostRecentDate;
      form.useMostRecentDateCutoff = form.useMostRecentDate ? useMostRecentDateCutoff ?? null : null;
      form.fixedFeeAllocationMode = fixedFeeAllocationMode;
      form.accounts = accounts;
      return form;
    },
    compare(state) {
      state.me.company.settings.report = state.me.company.settings.report || {};
      const {
        originatingContribution,
        responsibleContribution,
        originatingContributionFirm,
        workingContribution,
        useWorkingPercentage,
        originationStepDownPeriodInMonths,
        servicesToResponsible,
        chargesAttributedToResponsible,
        unallocatedToOriginating,
        creditsAsRevenue,
        useMostRecentDate,
        useMostRecentDateCutoff
      } = state.me.company.settings.report;
      const {
        fixedFeeAllocationMode
      } = state.me.company.settings;
      const savedServicesToResponsible = !!servicesToResponsible;
      const savedChargesAttributedToResponsible = !!chargesAttributedToResponsible;
      const savedUnallocatedToOriginating = !!unallocatedToOriginating;
      const savedCreditsAsRevenue = !!creditsAsRevenue;
      const savedUseMostRecentDate = !!useMostRecentDate;
      const formUseMostRecentDate = !!state.form.useMostRecentDate;
      const savedUseMostRecentDateCutoff = savedUseMostRecentDate ? useMostRecentDateCutoff ?? null : null;
      const formUseMostRecentDateCutoff = formUseMostRecentDate ? state.form.useMostRecentDateCutoff ?? null : null;
      if (originatingContribution !== state.form.originatingContribution) {
        return true;
      }
      if (responsibleContribution !== state.form.responsibleContribution) {
        return true;
      }
      if (originatingContributionFirm !== state.form.originatingContributionFirm) {
        return true;
      }
      if (workingContribution !== state.form.workingContribution) {
        return true;
      }
      if (useWorkingPercentage !== state.form.useWorkingPercentage) {
        return true;
      }
      if (originationStepDownPeriodInMonths !== state.form.originationStepDownPeriodInMonths) {
        return true;
      }
      if (savedServicesToResponsible !== !!state.form.servicesToResponsible) {
        return true;
      }
      if (savedChargesAttributedToResponsible !== !!state.form.chargesAttributedToResponsible) {
        return true;
      }
      if (savedUnallocatedToOriginating !== !!state.form.unallocatedToOriginating) {
        return true;
      }
      if (savedCreditsAsRevenue !== !!state.form.creditsAsRevenue) {
        return true;
      }
      if (savedUseMostRecentDate !== formUseMostRecentDate) {
        return true;
      }
      if (savedUseMostRecentDateCutoff !== formUseMostRecentDateCutoff) {
        return true;
      }
      if (fixedFeeAllocationMode !== state.form.fixedFeeAllocationMode) {
        return true;
      }
      if (state.tableDataChanged) {
        return true;
      }
      return false;
    }
  }
});